/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */
import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Radio from 'backbone.radio';
import html2pdf from 'html2pdf.js';

import showSuccessMessage from '@/util/ShowSuccessMessage';
import { validateForm } from '@/util/ParsleyUtils';
import { userHasRole } from '@/util/RoleUtils';
import OrderPackageStatuses from '@/enums/OrderPackageStatuses';

import ApproveReturnOrderPackage from '../../services/ApproveReturnOrderPackage';
import SaveOrderPackageEPCDecision from '../../services/SaveOrderPackageEPCDecision';
import OrdersPrintCollectionLoader from '../../../order/loaders/OrdersPrintCollectionLoader';
import OrdersPrintCollectionView from '../../../order/views/OrdersPrintCollectionView/OrdersPrintCollectionView';

import tmplOrderPackageDecisionView from './OrderPackageDecisionView.hbs';

const OrderPackageDecisionView = View.extend({
  template: tmplOrderPackageDecisionView,

  className: 'order-decision-view',

  ui: {
    epcDecisionStatus: '.epc-decision',
    saveBtn: '.save-button',
    submitBtn: '.submit-button',
    decisionStatusError: '#decisionStatusError',
    generatePDF: '.generate-pdf',
    vendorName: '#vendorName',
    vendorEmail: '#vendorEmail',
    vendorPhone: '#vendorPhone',
    explanation: '#explanationTextArea',
    explanationLabel: 'label[for="explanationTextArea"]',
    return: '#epcDecisionReturn',
    submitToVendor: '#epcDecisionSubmittedToVendor',
    vendorEmailLabel: 'label[for="vendorEmail"]'
  },

  regions: {
    generatePDF: '@ui.generatePDF'
  },

  events: {
    'click input[type="radio"]': 'onChangeDecision'
  },

  templateContext() {
    const decisionStatus = this.model.get('orderPackage').epcDecisionStatusID;
    const orderPackageStatus = this.model.get('orderPackage').statusID;
    return {
      returnChecked: decisionStatus === OrderPackageStatuses.RETURNED,
      approveChecked: decisionStatus === OrderPackageStatuses.APPROVED,
      completeChecked: decisionStatus === OrderPackageStatuses.COMPLETE,
      isEPCUser: userHasRole('EPC'),
      showSubmitToVendor:
        (orderPackageStatus, orderPackageStatus === OrderPackageStatuses.EPC_REVIEW) ||
        (decisionStatus, decisionStatus === OrderPackageStatuses.SUBMITTED_TO_VENDOR),
      hasVendorEmailSentOn: !!this.model.get('orderPackage').vendorEmailSentOn
    };
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  initialize() {
    this.model.set('isRelocation', this.model.get('orderPackage').formCode === 'RELOCATION');
  },

  onChangeDecision() {
    if (this.ui.return.prop('checked') === true ) {
      this.ui.explanation.attr('data-parsley-required', true);
      this.ui.explanationLabel.toggleClass('required', true);
      this.ui.explanation.prop('required', true);

      this.ui.vendorEmail.removeAttr('data-parsley-required');
      this.ui.vendorEmail.prop('required', false);
      this.ui.vendorEmailLabel.toggleClass('required', false);
    } else if (this.ui.submitToVendor.prop('checked') === true) {
      this.ui.explanation.removeAttr('data-parsley-required');
      this.ui.explanationLabel.toggleClass('required', false);
      this.ui.explanation.prop('required', false);

      this.ui.vendorEmail.attr('data-parsley-required', 'true');
      this.ui.vendorEmail.prop('required', true);
      this.ui.vendorEmailLabel.toggleClass('required', true);
    }
  },

  onRender() {
    const orderPackage = this.model.get('orderPackage');

    const decisionData = {
      decisionStatus: orderPackage.epcDecisionStatusID,
      explanation: orderPackage.epcDecisionExplanation,
      vendorName: orderPackage.vendorName,
      vendorEmail: orderPackage.vendorEmail,
      vendorPhone: orderPackage.vendorPhone
    };

    Syphon.deserialize(this, decisionData);

    if (!this.options.editable) {
      this.toggleForm(false);
    }
    if (decisionData.decisionStatus === '4') {
      this.ui.return.attr('checked', true).trigger('click');
    } else if (decisionData.decisionStatus === '9') {
      this.ui.submitToVendor.attr('checked', true).trigger('click');
    }
  },

  async onSave(e) {
    if (this.options.editable) {
      this.decisionFormData = Syphon.serialize(this.ui.epcDecisionStatus);
      this.toggleForm(false);
      if (userHasRole('EPC')) {
        try {
          await SaveOrderPackageEPCDecision({
            orderPackageID: this.options.orderPackageID,
            epcDecisionStatusID: this.decisionFormData.decisionStatus,
            epcDecisionExplanation: this.decisionFormData.explanation,
            vendorName: this.decisionFormData.vendorName,
            vendorEmail: this.decisionFormData.vendorEmail,
            vendorPhone: this.decisionFormData.vendorPhone
          });

          showSuccessMessage({
            code: 'saveSuccess',
            title: 'Save Success',
            message: 'Order Package EPC Decision Saved.',
            hideAfter: 1
          });
        } catch (err) {
          throw new Error(err);
        }
      }
      this.toggleForm(true);
    }
  },

  async onSubmit(e) {
    this.decisionFormData = Syphon.serialize(this.ui.epcDecisionStatus);
    try {
      await validateForm(this.ui.epcDecisionStatus);
      this.toggleForm(false);

      if (userHasRole('EPC') && this.model.get('orderPackage').statusID === OrderPackageStatuses.EPC_REVIEW) {
        await this.handleEPCSubmission();
      }

      this.options.editable = false;
      await Radio.channel('navigate').request('show:int:dashboard');
    } catch (err) {
      this.toggleForm(true); // Only enable form if service failed
    }
  },

  async handleEPCSubmission() {
    //Only EPCs can Approve/Return order packages.
    let attachments;
    const isRelocation = this.model.get('isRelocation');

    //for relocation sending to vendor
    if (isRelocation && this.decisionFormData.decisionStatus === OrderPackageStatuses.SUBMITTED_TO_VENDOR) {
      const packagedOrders = this.model.get('packagedOrders');
      this.packagedOrdersCollection = new Backbone.Collection(packagedOrders);

      const collectionView = await OrdersPrintCollectionLoader(this.packagedOrdersCollection);

      this.ordersPrintCollectionView = new OrdersPrintCollectionView({
        collection: collectionView,
        itemsRendered: 0
      });
      this.ordersPrintCollectionView.render();

      attachments = await this.generateAttachments(this.ordersPrintCollectionView.children._views);
      const attachmentsObject = {
        attachments: attachments.map((attachment) => ({
          attachment: {
            orderId: attachment.orderId,
            file: attachment.file
          }
        }))
      };
      attachments = attachmentsObject.attachments;
    }
    await ApproveReturnOrderPackage(
      this.options.orderPackageID,
      this.decisionFormData.decisionStatus,
      this.decisionFormData.explanation,
      this.decisionFormData.vendorName,
      this.decisionFormData.vendorEmail,
      this.decisionFormData.vendorPhone,
      attachments
    );

    showSuccessMessage({
      code: 'submitSuccess',
      title: 'Submit Success',
      message: 'Order Package Decision Submitted.',
      hideAfter: 5
    });
  },

  async generateAttachments(views) {
    const attachments = [];

    for (const childView of views) {
      const model = childView.model;
      const innerHTML = childView.el.outerHTML;

      const orderId = model.get('orderId');

      const pdfBase64String = await this.generatePDF(innerHTML);

      attachments.push({ orderId, file: pdfBase64String });
    }

    return attachments;
  },

  async generatePDF(html) {
    const options = {
      filename: 'downloaded-page.pdf',
      html2canvas: { scale: 3, letterRendering: true },
      margin: [10, 5, 5, 5],
      image: { type: 'jpeg', quality: 0.8 },
      pagebreak: {
        mode: ['avoid-all', 'css']
      },
      jsPDF: { unit: 'mm', format: 'letter', compress: true, orientation: 'portrait' }
    };

    const pdf = await html2pdf().set(options).from(html).outputPdf();
    const base64String = window.btoa(pdf);
    return base64String;
  }
});

export default OrderPackageDecisionView;
